// src/components/features/chat/ChatWindow.jsx
//
// Sprint 13 v4 — Figma redesign: teal header, new quick actions, disclaimer.
//
// Figma source: dark-take-47011126.figma.site
//
// Changes from v3:
//   - Header: teal sparkle avatar (was gold stethoscope emoji)
//   - Quick actions: 3 buttons — "Book appointment" (filled), "Order lab test",
//     "View care plan" (replaces 4-button set)
//   - Quick actions PINNED between scroll area and input bar (always visible)
//   - Disclaimer: "Always verify with your care team." pinned at bottom
//   - Send button: teal accent (was gold)
//   - 2-step clear confirmation preserved
//   - useNavigate() preserved (SPA navigation)
//
// React expert review:
//   ✅ type="button" on all buttons
//   ✅ aria-label on all interactive elements
//   ✅ No window.confirm(), no window.alert()
//   ✅ useNavigate() for SPA navigation
//   ✅ Stable msgTimestamps via useRef
//   ✅ lastDateLabel in useMemo

import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MessageBubble from './MessageBubble';
import WelcomeBubble from './WelcomeBubble';
import AssistantMark, { SparkleIcon } from './AssistantMark';

// Themed via var(--token) throughout — this used to be two local hardcoded
// palettes (T for teal, C for navy/cream/gray) that never changed with the
// patient's theme, which is why this whole screen stayed light/cream even
// when the patient was in DARK: the background literally never read
// --bg-canvas. --teal-dark/-mid/-light/-bg are defined in index.css
// alongside --teal itself.

function formatTime(date) {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatDateLabel(date) {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return date.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
}

function DateSeparator({ label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0 16px' }}>
      <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
      <span style={{
        fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase',
        letterSpacing: '0.8px', background: 'var(--bg-canvas)', padding: '3px 10px',
        borderRadius: 20, border: '1px solid var(--border)',
      }}>{label}</span>
      <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
    </div>
  );
}

// ─── Sparkle SVG (header avatar + banner) ────────────────────────────────────
// ─── Quick action buttons (Figma: 3 equal buttons at bottom) ─────────────────
const QUICK_ACTIONS = [
  { label: '📅 Book appointment',  text: "I'd like to book an appointment with a doctor.", primary: true },
  { label: '🧪 Order lab test',    text: 'I want to order a lab test.',                    primary: false },
  { label: '📋 View care plan',    text: 'Can you show me my care plan?',                  primary: false },
];

function QuickActions({ onSelect, disabled }) {
  return (
    <div className="cw-quick-actions" style={{ display: 'flex', gap: 8, padding: '8px 0 14px' }}>
      {QUICK_ACTIONS.map(a => (
        <button
          type="button"
          key={a.text}
          onClick={() => onSelect(a.text)}
          disabled={disabled}
          aria-label={a.label}
          style={{
            flex: '1 1 0',
            padding: '10px 6px',
            borderRadius: 10,
            border: a.primary ? 'none' : '1.5px solid var(--teal-light)',
            background: disabled
              ? 'var(--bg-subtle)'
              : a.primary
                ? 'var(--teal)'
                : 'var(--teal-bg)',
            // --brand-dark, not white/'#fff': --teal is bright mint in dark
            // theme, and white-on-bright-mint is low contrast — same reasoning
            // as the Care Team page's active tab (--brand-dark stays a dark
            // navy against --teal in both themes).
            color: disabled
              ? 'var(--text-muted)'
              : a.primary
                ? 'var(--brand-dark)'
                : 'var(--teal)',
            fontSize: 12,
            fontWeight: 500,
            cursor: disabled ? 'not-allowed' : 'pointer',
            transition: 'all 0.15s',
            fontFamily: 'inherit',
            minHeight: 44,
            minWidth: 0,
            textAlign: 'center',
            lineHeight: 1.3,
            wordBreak: 'break-word',
          }}
          onMouseEnter={e => {
            if (!disabled) {
              e.currentTarget.style.opacity = '0.85';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(13,148,136,0.25)';
            }
          }}
          onMouseLeave={e => {
            if (!disabled) {
              e.currentTarget.style.opacity = '1';
              e.currentTarget.style.boxShadow = 'none';
            }
          }}
        >
          {a.label}
        </button>
      ))}
    </div>
  );
}

// ─── Main ChatWindow ─────────────────────────────────────────────────────────
export default function ChatWindow({
  displayMessages, isLoading, onSend, onUpload, onConfirmUploadType, onClear, patient, onClose, compact,
  welcomeSummary, initialPrompt, autoSend = false, summaryReady = true, assistantMode = 'LIGHT',
}) {
  // LIGHT (default) is the simplified assistant screen — no quick-action
  // shortcuts or stat cards, since HEAVY covers that ground with the full
  // external chat experience instead (see PatientSettingsPage).
  const isLightMode = assistantMode !== 'HEAVY';
  // summaryReady=false means triage context is still loading — disable input
  // so the patient can't accidentally send a message with null clinical context.
  const inputDisabled = isLoading || !summaryReady;
  const [input, setInput]               = useState('');
  const [clearPending, setClearPending] = useState(false);
  const [uploadNotice, setUploadNotice] = useState(''); // inline validation error — no window.alert()
  const bottomRef                       = useRef(null);
  const textareaRef                     = useRef(null);
  const fileInputRef                    = useRef(null);
  const clearTimeoutRef                 = useRef(null);
  const isEmpty                         = displayMessages.length === 0;
  const navigate                        = useNavigate();

  const MAX_FILE_MB = 20; // matches PatientUploadPage.jsx's dedicated upload flow
  const ACCEPTED_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];

  function handleAttachClick() {
    if (inputDisabled || !onUpload) return;
    fileInputRef.current?.click();
  }

  function handleFileChange(e) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file next time
    if (!file) return;

    if (!ACCEPTED_TYPES.includes(file.type)) {
      setUploadNotice('Please upload a PDF, JPG, or PNG file.');
      return;
    }
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      setUploadNotice(`File too large — maximum ${MAX_FILE_MB}MB.`);
      return;
    }
    setUploadNotice('');
    onUpload?.(file);
  }

  // Stable timestamps
  const msgTimestampsRef = useRef(new Map());
  displayMessages.forEach((_, i) => {
    if (!msgTimestampsRef.current.has(i)) msgTimestampsRef.current.set(i, new Date());
  });

  // Pre-compute date separators
  const dateSeparators = useMemo(() => {
    const result = [];
    let lastLabel = null;
    displayMessages.forEach((_, i) => {
      const ts = msgTimestampsRef.current.get(i) ?? new Date();
      const label = formatDateLabel(ts);
      if (label !== lastLabel) { result.push({ index: i, label }); lastLabel = label; }
    });
    return result;
  }, [displayMessages.length]);

  // autoSend (set by the Today screen's "Ask about your health story" bar —
  // the patient's own already-typed query, unlike a suggested question from
  // a report page) sends the carried-over prompt itself instead of just
  // pre-filling the textarea. Held off until summaryReady so it isn't sent
  // with the triage context still null, and autoSentRef guards against
  // re-sending if inputDisabled flips again later (e.g. a follow-up message
  // going out).
  const autoSentRef = useRef(false);
  useEffect(() => {
    if (!initialPrompt) return;
    if (autoSend) {
      if (!inputDisabled && !autoSentRef.current) {
        autoSentRef.current = true;
        onSend(initialPrompt);
      }
    } else {
      setInput(initialPrompt);
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [initialPrompt, autoSend, inputDisabled, onSend]);

  // Keep the newest content in view without the screen bouncing. The old
  // smooth scrollIntoView fired on every streamed update, so overlapping
  // smooth animations (plus the tool chip and reply bubble changing height
  // together) made the chat jump up and down. Now: one instant scroll of this
  // container only, and only while the patient is already at the bottom — or
  // when they have just sent a message — so reading earlier messages is never
  // interrupted.
  const scrollAreaRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const handleScrollArea = useCallback(() => {
    const el = scrollAreaRef.current;
    if (!el) return;
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  }, []);

  useEffect(() => {
    const el = scrollAreaRef.current;
    if (!el) return;
    const last = displayMessages[displayMessages.length - 1];
    if (last?.role === 'user') stickToBottomRef.current = true;
    if (stickToBottomRef.current) el.scrollTop = el.scrollHeight;
  }, [displayMessages]);

  const handleNavigate = useCallback((path) => { navigate(path); }, [navigate]);

  function handleSend() {
    const trimmed = input.trim();
    if (!trimmed || inputDisabled) return;
    onSend(trimmed);
    setInput('');
    textareaRef.current?.focus();
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  }

  function handleClearClick() {
    if (clearPending) {
      setClearPending(false);
      if (clearTimeoutRef.current) clearTimeout(clearTimeoutRef.current);
      onClear();
    } else {
      setClearPending(true);
      clearTimeoutRef.current = setTimeout(() => setClearPending(false), 3000);
    }
  }

  useEffect(() => () => { if (clearTimeoutRef.current) clearTimeout(clearTimeoutRef.current); }, []);

  function getDateLabel(idx) {
    return dateSeparators.find(s => s.index === idx)?.label || null;
  }

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0,
      boxSizing: 'border-box',
      background: 'var(--bg-canvas)',
      fontFamily: 'var(--font-body)',
      borderRadius: compact ? 16 : 0, overflow: 'hidden',
    }}>

      {/* ── Header (Figma: dark with teal chat-bubble avatar) — --brand-dark,
          not --bg-canvas: this chrome stays dark in both themes, same as
          the app's own topbar/sidebar. ── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: compact ? '12px 16px' : '12px 20px',
        background: 'var(--brand-dark)', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Teal chat-bubble avatar — the same mark the assistant's
              messages carry, from AssistantMark.jsx */}
          <AssistantMark size={compact ? 34 : 38} solid />
          <div>
            <div style={{ fontWeight: 700, fontSize: compact ? 13 : 15, color: 'var(--text-on-brand-dark)' }}>
              Ask Previu
            </div>
            {/* Says what this is. It used to read "Online · Hi <name>", which
                borrowed the presence language of a person on shift — in an app
                that also has a real human care-team chat, that is the one thing
                this line must not be ambiguous about. The greeting moved out
                too: the welcome bubble already opens with "Hello, <name> 👋". */}
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)' }}>
              AI assistant · replies instantly
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            type="button"
            onClick={handleClearClick}
            aria-label={clearPending ? 'Confirm clear chat' : 'Clear chat'}
            style={{
              background: clearPending ? 'rgba(192,57,43,0.25)' : 'transparent',
              border: 'none',
              color: clearPending ? 'var(--red)' : 'rgba(255,255,255,0.7)',
              cursor: 'pointer', fontSize: 13, fontWeight: 500,
              padding: '4px 10px', borderRadius: 6, fontFamily: 'inherit',
              transition: 'all 0.15s', minHeight: 28,
            }}
          >
            {clearPending ? 'Confirm?' : 'Clear'}
          </button>
          {/* RD avatar shown in Figma — handled by PatientAppShell topbar, not here */}
        </div>
      </div>

      {/* ── Scrollable message area ── */}
      {/* 18 Jul 2026: added minHeight: 0 — same fix applied to the newer
          human-to-human HumanChatWindow.jsx after a mobile bug there. A flex
          child with flex:1 + overflow:auto but no min-height:0 defaults to
          min-height:auto, meaning it won't shrink below its own content's
          height — so instead of scrolling internally it just grows to fit
          every message, pushing the input bar (and the whole page) below
          the visible viewport. This is a DIFFERENT bug from the earlier
          "quick actions pinned outside the scroll area" fix a few lines
          below — that one was about placement (quick actions were INSIDE
          the scroll area); this one is a missing flex property that can
          cause the same symptom even with correct placement.

          Also added overflow-anchor: none — found via a precise repro
          (welcome bubble renders correctly, but breaks specifically after
          sending the first message and scrolling up). The QuickActions
          block a few lines below only renders while isEmpty===true; the
          instant a message is sent it unmounts entirely, resizing this
          scroll area's available height while the user may still be
          scrolled up. Browsers' automatic scroll-anchoring — meant to keep
          the same content in view when layout shifts nearby — can produce
          exactly this kind of broken/overlapping-looking render in that
          situation. overflow-anchor:none disables that behavior for this
          container so a resize doesn't fight with the current scroll
          position. */}
      <div ref={scrollAreaRef} onScroll={handleScrollArea} style={{
        flex: 1, minHeight: 0, overflowY: 'auto', overflowAnchor: 'none',
        padding: compact ? '12px 14px' : '16px 20px',
        display: 'flex', flexDirection: 'column',
        background: 'var(--bg-canvas)',
      }}>
        <div className="cw-col" style={{ width: '100%', margin: '0 auto' }}>
          {/* Welcome bubble */}
          {!compact && (
            <div style={{ marginBottom: 16 }}>
              <WelcomeBubble
                patient={patient}
                summaryData={welcomeSummary}
                onNavigate={handleNavigate}
                isLightMode={isLightMode}
              />
            </div>
          )}

          {/* Compact empty state */}
          {isEmpty && compact && (
            <div style={{ margin: 'auto', textAlign: 'center', maxWidth: 260 }}>
              <div style={{
                width: 56, height: 56, borderRadius: '50%', background: 'var(--teal-bg)',
                border: '2px solid var(--teal-light)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 12px',
              }}>
                <SparkleIcon size={24} color="var(--teal)" />
              </div>
              <div style={{ fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6, fontSize: 15 }}>
                How can I help you today?
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.6 }}>
                Book appointments, order lab tests, or update your details.
              </div>
            </div>
          )}

          {/* Messages */}
          {displayMessages.map((msg, i) => {
            const ts = msgTimestampsRef.current.get(i) ?? new Date();
            const dateLabel = getDateLabel(i);
            // The avatar marks the end of a run of assistant messages, not every
            // one of them: a reply that arrives as text + a widget + a card is
            // one turn from one speaker.
            const nextIsAssistant = displayMessages[i + 1]
              && displayMessages[i + 1].role !== 'user';
            return (
              <React.Fragment key={i}>
                {dateLabel && <DateSeparator label={dateLabel} />}
                <MessageBubble
                  message={msg}
                  timestamp={formatTime(ts)}
                  compact={compact}
                  onConfirmUploadType={onConfirmUploadType}
                  showAvatar={!nextIsAssistant}
                />
              </React.Fragment>
            );
          })}

          {/* Quick actions removed from here — now pinned below scroll area */}

          <div ref={bottomRef} />
        </div>
      </div>

      {/* ── Quick actions — PINNED between scroll area and input bar ── */}
      {isEmpty && !isLightMode && (
        <div style={{
          padding: compact ? '0 14px' : '0 20px',
          background: 'var(--bg-canvas)', flexShrink: 0,
          borderTop: `1px solid var(--border)`,
          overflowX: 'hidden',
        }}>
          <div className="cw-col" style={{ width: '100%', margin: '0 auto' }}>
            <QuickActions onSelect={onSend} disabled={inputDisabled} />
          </div>
        </div>
      )}

      {/* ── Inline upload validation notice (no window.alert per checklist) ── */}
      {uploadNotice && (
        <div style={{
          padding: '8px 16px', background: 'var(--red-bg)',
          borderTop: `1px solid var(--border)`, flexShrink: 0,
        }}>
          <p style={{
            fontFamily: 'inherit', fontSize: 12, color: 'var(--red)',
            margin: 0, textAlign: 'center',
          }}>
            ⚠ {uploadNotice}
          </p>
        </div>
      )}

      {/* ── Input bar ── */}
      <div style={{
        padding: compact ? '10px 12px' : '12px 16px',
        borderTop: `1px solid var(--border)`,
        background: 'var(--bg-surface)',
        display: 'flex', gap: 8, alignItems: 'flex-end', flexShrink: 0,
      }}>
        {onUpload && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleFileChange}
              style={{ display: 'none' }}
              aria-hidden="true"
              tabIndex={-1}
            />
            <button
              type="button"
              onClick={handleAttachClick}
              disabled={inputDisabled}
              aria-label="Attach a lab report"
              title="Attach a lab report"
              style={{
                width: 44, height: 44, borderRadius: '50%', border: '1.5px solid var(--border)',
                background: inputDisabled ? 'var(--bg-subtle)' : 'var(--bg-surface)',
                color: inputDisabled ? 'var(--text-muted)' : 'var(--teal)',
                cursor: inputDisabled ? 'not-allowed' : 'pointer',
                fontSize: 18, display: 'flex', alignItems: 'center',
                justifyContent: 'center', flexShrink: 0, transition: 'all 0.2s',
              }}
            >
              📎
            </button>
          </>
        )}
        <textarea
          ref={textareaRef}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={1}
          disabled={inputDisabled}
          placeholder={summaryReady ? 'Type a message…' : 'Preparing your health context…'}
          aria-label="Type your health question"
          style={{
            flex: 1, resize: 'none',
            border: `1.5px solid var(--border)`, borderRadius: 22,
            padding: compact ? '9px 14px' : '11px 16px',
            fontSize: compact ? 13 : 14, fontFamily: 'inherit', outline: 'none',
            background: 'var(--bg-subtle)', color: 'var(--text-primary)',
            lineHeight: 1.5, maxHeight: 100, overflowY: 'auto',
            transition: 'border-color 0.2s, box-shadow 0.2s',
          }}
          onFocus={e => {
            e.target.style.borderColor = 'var(--teal)';
            e.target.style.boxShadow = `0 0 0 3px var(--teal-bg)`;
            e.target.style.background = 'var(--bg-surface)';
          }}
          onBlur={e => {
            e.target.style.borderColor = 'var(--border)';
            e.target.style.boxShadow = 'none';
            e.target.style.background = 'var(--bg-subtle)';
          }}
        />
        <button
          type="button"
          onClick={handleSend}
          disabled={!input.trim() || inputDisabled}
          aria-label={isLoading ? 'Sending message' : summaryReady ? 'Send message' : 'Loading health context'}
          style={{
            width: 44, height: 44, borderRadius: '50%', border: 'none',
            background: !input.trim() || inputDisabled
              ? 'var(--border)'
              : 'var(--teal)',
            // --brand-dark, not white — see QuickActions' primary button for
            // the same --teal-contrast reasoning.
            color: !input.trim() || inputDisabled ? 'var(--text-muted)' : 'var(--brand-dark)',
            cursor: !input.trim() || inputDisabled ? 'not-allowed' : 'pointer',
            fontSize: 18, display: 'flex', alignItems: 'center',
            justifyContent: 'center', flexShrink: 0, transition: 'all 0.2s',
            boxShadow: !input.trim() || inputDisabled ? 'none' : '0 2px 8px rgba(13,148,136,0.35)',
          }}
        >
          {isLoading
            ? <span style={{ width: 16, height: 16, border: '2px solid var(--text-muted)', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />
            : <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
          }
        </button>
      </div>

      {/* ── Disclaimer (Figma: "Always verify with your care team.") ── */}
      <div style={{
        padding: '6px 16px 10px', background: 'var(--bg-surface)',
        borderTop: `1px solid var(--border)`, flexShrink: 0,
      }}>
        <p style={{
          fontFamily: 'inherit', fontSize: 11, color: 'var(--text-muted)',
          margin: 0, textAlign: 'center', lineHeight: 1.5,
        }}>
          Always verify with your care team.
        </p>
      </div>

      {/* ── Styles ── */}
      {/* No @import for DM Sans here — it's already loaded globally via
          public/index.html and set as --font-body; this component now uses
          that var() (see the root container's fontFamily above) rather than
          re-fetching the same font a second time under a hardcoded name. */}
      <style>{`
        @keyframes cursor-blink { 0%,100%{opacity:1} 50%{opacity:0} }
        @keyframes dot-bounce   { 0%,80%,100%{transform:translateY(0)} 40%{transform:translateY(-4px)} }
        @keyframes spin         { to{transform:rotate(360deg)} }
        @keyframes slide-up     { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        .cw-col { max-width: 680px; }
        @media (max-width: 768px) { .cw-col { max-width: 100% !important; } }
        @media (max-width: 400px) {
          .cw-quick-actions { gap: 6px !important; }
          .cw-quick-actions button { font-size: 11px !important; padding: 8px 4px !important; }
        }
        /* 18 Jul 2026 fix: this was max-width: 360px, which doesn't cover
           ANY standard phone width (iPhones/most Android phones are
           375-430px CSS width) — so this fallback, the only one that
           actually prevents 3 buttons overflowing the viewport, was never
           firing on a real device. Every phone just got the smaller-font
           ROW layout above, which still doesn't fit "Book appointment" /
           "Order lab test" / "View care plan" side by side, overflowing
           the viewport width and dragging the composer/send button along
           with it via page-wide horizontal scroll.

           Also added button { flex: none } — the EXACT same bug just found
           and fixed in WelcomeBubble.jsx's StatWidget: each button's
           flex: '1 1 0' (set in QuickActions below) was written for ROW
           mode, dividing available WIDTH equally. Once flex-direction
           switches to column here, that same property divides available
           HEIGHT equally instead, with flex-basis:0 telling each button to
           ignore its own natural content height — stretching all 3 buttons
           to huge, oversized heights instead of sizing to their text.
           flex:none lets each one size to its own content in column mode. */
        @media (max-width: 480px) {
          .cw-quick-actions { flex-direction: column !important; gap: 6px !important; }
          .cw-quick-actions button { flex: none !important; font-size: 13px !important; padding: 10px 12px !important; text-align: left !important; }
        }
      `}</style>
    </div>
  );
}
